type RGB = `rgb(${string})`;
type RGBA = `rgba(${string})`;
type HEX = `#${string}`;
type HSL = `hsl(${string})`;
type HSLA = `hsla(${string})`;
type VAR = `var(${string})`;

type CssGlobals = "inherit" | "initial" | "revert" | "unset";

export type CssColor = "currentColor" | "transparent" | RGB | RGBA | HEX | HSL | HSLA | VAR | CssGlobals;

export type CurrencyLocale =
  | "auto"
  | "en-US"
  | "en_US"
  | "da-DK"
  | "da_DK"
  | "sv-SE"
  | "sv_SE"
  | "de-DE"
  | "de_DE"
  | "nl-NL"
  | "nl_NL"
  | "en-GB"
  | "en_GB"
  | "pl-PL"
  | "pl_PL";

export interface PreoStorefrontAttributes {
  // --- General ---

  /**
   * @title Preo event edition SDK ID
   * @description The SDK ID of the event edition can be found in the backoffice.
   * @example "dev:eu:demo:edt_2ZrMmEES8YQSL6VHSICrTwGwC04"
   */
  sdkId: string;

  /**
   * @title Locale
   * @description The locale to use for the storefront.
   * @default "en"
   */
  locale?: string;

  /**
   * @title Tracking consent
   * @description When true, marketing pixels (Meta, TikTok, GA, GTM, Matomo) will be loaded. Set to false until user gives cookie consent.
   * @default false
   */
  trackingConsent?: boolean;

  /**
   * @title Analytics integrations
   * @description Set to "auto" to detect existing pixels on the page (GA, GTM, Meta, TikTok).
   * Or provide a JSON array of integrations: [{"service": "ga", "metadata": {"gaTrackingId": "G-XXX"}}, ...]
   * @default ""
   */
  analyticsIntegrations?: string;

  /**
   * @title TTL for store
   * @description Time to live for the store in minutes.
   * @default 60
   */
  ttlStore?: number;

  /**
   * @title Authentication method
   * @description The authentication method to use for the storefront.
   * @type {'form'| 'sso'}
   * @efault "form"
   */
  authMethod?: "form" | "sso";

  // --- Product selection ---

  /**
   * @title Product categories
   * @description A comma-separated list of product categories to show. If empty, all products will be shown.
   * @default ""
   */
  mainCategories?: string;

  /**
   * @title Product IDs
   * @description A comma-separated list of product IDs to show. If empty, all products will be shown.
   * @default ""
   */
  saleProductIds?: string;

  /**
   * @title Multi-step product categories
   * @description A comma-separated list of product categories to show in a multi-step product selection. If empty, all products will be shown in a single step.
   * @default ""
   */
  crossSellCategories?: string;

  // --- Product presentation---

  /**
   * @title Product image mode
   * @description Determines behavior of product images.
   * @default "zoom"
   */
  productImageMode?: "zoom" | "static" | "full";

  /**
   * @title Use only short descriptions
   * @description When enabled, "read more" below product description is hidden and only short product description is shown.
   * @default false
   */
  readMoreButtonDisabled?: boolean;

  /**
   * @title Crossed price layout
   * @description Style of presentation for crossed prices.
   * @default "full"
   */
  crossedPriceLayout?: "full" | "compact" | "none";

  /**
   * @title Override attributes from query params
   * @description When enabled, component will override its attributes from query params.
   * @default false
   */
  overrideFromQueryParams?: boolean;

  // --- Cart ---

  /**
   * @title Hide cart summary on checkout page
   * @description When enabled, summary of items in the cart won't be shown on the checkout page.
   * @default false
   */
  hideCartSummary?: boolean;

  /**
   * @title Disable cart persistence
   * @description When enabled, cart contents won't be persisted between page reloads. This is useful when having multiple landing pages with different products offered for the same event edition.
   * @default false
   */
  disableLocalStorageCart?: boolean;

  /**
   * @title Discount input
   * @description When enabled, discount input will be shown at final summary
   * @default false
   */
  showDiscount?: boolean;

  /**
   * @title Empty cart is allowed
   * @description When enabled, empty cart is allowed and user can proceed to checkout with empty cart.
   * @default false
   */
  emptyCartAllowed?: boolean;

  /**
   * @title Cart processing locally
   * @description When enabled, cart processing will be done locally
   * @default false
   */
  isCartProcessingLocally?: boolean;

  // --- Checkout (basic) ---

  /**
   * @title Hide discount
   * @description When enabled, the discount won't be shown at checkout.
   * @default false
   */
  hideDiscount?: boolean;

  // --- Checkout (advanced) ---

  /**
   * @title Checkout success URL
   * @description The URL to redirect to after a successful checkout.
   * @default null
   */
  checkoutSuccessUrl?: string;

  /**
   * @title Checkout cancel URL
   * @description The URL to redirect to after a cancelled checkout.
   * @default null
   */
  checkoutCancelUrl?: string;

  // --- UI: Footer ---

  /**
   * @title Hide footer
   * @description When enabled, the footer won't be shown.
   * @default false
   */
  hideFooter?: boolean;

  /**
   * @title Hide language selector
   * @description When enabled, the language selector in the footer won't be shown.
   * @default false
   */
  hideLanguageSelector?: boolean;

  // --- UI: Tweaks ---

  /**
   * @title Sticky summary
   * @description When enabled, the purchase summary will stick to the bottom of the page.
   * @default false
   */
  stickySummary?: boolean;

  /**
   * @title Currency locale
   * @description Determines formatting of prices.
   * @default "auto"
   */
  currencyLocale?: CurrencyLocale;

  /**
   * @title Catalogue mode
   * @description When enabled, the product list will be shown in a grid view.
   * @default false
   */
  isCatalogue?: boolean;

  /**
   * @title Scroll to top
   * @description When enabled, the page will scroll to the top when the user clicks on the logo.
   * @default false
   */
  scrollIntoView?: boolean;

  // --- UI: Colors ---

  colorBrand?: CssColor;
  colorComplementary?: string;
  colorSection?: string;
  colorTextPrimary?: string;
  colorTextSecondary?: string;
  colorTextButton?: string;
  colorTextButtonDisabled?: string;
  colorButtonDisabled?: string;
  colorProductBackgroundImage?: string;
  colorSectionDiscount?: string;
  colorSectionDiscountTextPrimary?: string;
  colorSectionDiscountTextSecondary?: string;
  colorSectionDiscountIcon?: string;
  colorSectionDiscountBackgroundIcon?: string;
  colorTextFooter?: string;
  borderRadiusCard?: string;
  borderRadiusButton?: string;
  borderRadiusProduct?: string;
  borderRadiusInput?: string;
  borderRadiusFull?: string;

  // --- User form fields ---

  /**
   * @title First name field
   * @description When enabled, the first name field will be shown in the user form.
   * @default true
   */
  hasFirstNameField?: boolean;

  /**
   * @title Last name field
   * @description When enabled, the last name field will be shown in the user form.
   * @default true
   */
  hasLastNameField?: boolean;

  /**
   * @title Email field
   * @description When enabled, the email field will be shown in the user form.
   * @default true
   */
  hasEmailField?: boolean;

  /**
   * @title Confirm email field
   * @description When enabled, the confirm email field will be shown in the user form.
   * @default false
   */
  hasConfirmEmailField?: boolean;

  /**
   * @title Phone field
   * @description When enabled, the phone field will be shown in the user form.
   * @default true
   */
  hasPhoneField?: boolean;

  /**
   * @title Confirm phone field
   * @description When enabled, the confirm phone field will be shown in the user form.
   * @default false
   */
  hasConfirmPhoneField?: boolean;

  // --- Developer ---

  /**
   * @title Enable debug mode
   * @description When enabled, the components will log debug information to the console
   * @default false
   */
  debug?: boolean;

  // --- SSO ---

  /**
   * @title Authenticated user first name
   * @description The first name of the authenticated user.
   * @default ""
   */
  authUserFirstName: string;

  /**
   * @title Authenticated user last name
   * @description The last name of the authenticated user.
   * @default ""
   */
  authUserLastName: string;

  /**
   * @title Authenticated user email
   * @description The email of the authenticated user.
   * @default ""
   */
  authUserEmail: string;

  /**
   * @title Authenticated user phone
   * @description The phone of the authenticated user.
   * @default ""
   */
  authUserPhone: string;

  /**
   * @title SSO login URL
   * @description The URL to redirect to when the user clicks on the SSO login button.
   * @default ""
   */
  ssoLoginUrl: string;

  // --- Mess to clean up ---

  payButtonText?: string; // NOTE: used in giveaway component only
  includeDepositToPrice?: boolean; // NOTE: unused?

  /**
   * @title Age Gate Mode
   * @description Where age gate is shown before showing the product list (on_load) or where age gate is shown when product is being added to the cart (on_cart_add)
   * @default 'on_load'
   */
  ageGateMode?: "on_load" | "on_cart_add";

  /**
   * @title Age Gate Age Verification
   * @description Age to verify for age gate
   * @default '18'
   */
  ageGateAgeVerification?: number;

  /**
   * @title Age Gate Verification Method
   * @description Method to verify age gate, either using Criipto or modal
   * @default 'modal'
   */
  ageGateVerificationMethod?: "criipto" | "modal";

  /**
   * @title Restricted products are visible despite the restrictions after age gate rejection
   * @description When enabled, restricted products will be visible when the user rejects the age gate.
   * @default false
   */
  hasRestrictedProductsVisibleDespiteRestriction?: boolean;

  /**
   * @title Is cart summary editable
   * @description When enabled, cart summary is shown
   * @default true
   */
  isCartSummaryEditable?: boolean;

  /**
   * @title Has grouped products in summary
   * @description When enabled, products in the summary are grouped together and not editable, there is no division into variants and options
   * @default false
   */
  hasGroupedProductsInSummary?: boolean;
}

export const DEFAULT_ATTRIBUTES = {
  locale: "en",
  debug: false,
  checkoutSuccessUrl: "",
  checkoutCancelUrl: "",
  hideFooter: false,
  hideLanguageSelector: false,
  hideDiscount: false,
  authMethod: "form" as "form",
  readMoreButtonDisabled: false,
  productImageMode: "zoom" as PreoStorefrontAttributes["productImageMode"],
  stickySummary: false,
  hideCartSummary: false,
  includeDepositToPrice: false,
  disableLocalStorageCart: false,
  crossedPriceLayout: "full" as PreoStorefrontAttributes["crossedPriceLayout"],
  trackingConsent: false,
  analyticsIntegrations: "",
  ageGateMode: "on_load" as PreoStorefrontAttributes["ageGateMode"],
  ageGateVerificationMethod: "modal" as PreoStorefrontAttributes["ageGateVerificationMethod"],
  hasRestrictedProductsVisibleDespiteRestriction: false,
  showDiscount: false,
  overrideFromQueryParams: false,
  emptyCartAllowed: false,
  ageGateAgeVerification: 18,
  currencyLocale: "auto" as CurrencyLocale,
  isCartSummaryEditable: true,
  hasGroupedProductsInSummary: false,
  isCartProcessingLocally: false,
  ttlStore: 60,
  isCatalogue: false,
  scrollIntoView: false,
  // --- User form fields ---
  hasFirstNameField: true,
  hasLastNameField: true,
  hasEmailField: true,
  hasConfirmEmailField: false,
  hasPhoneField: true,
  hasConfirmPhoneField: false,
  // --- Authenticated user details ---
  authUserFirstName: "",
  authUserLastName: "",
  authUserEmail: "",
  authUserPhone: "",
  ssoLoginUrl: "",
};

export const PREDEFINED_COUNTRY_CODES = {
  1: "us",
  44: "gb",
} as {
  [key: number]: string;
};
